/**
 * GA4 event payload builders for the Contact Builder lead-form flow (NHS20-8253).
 *
 * Pure functions — no Redux, no DOM, no dispatch. Each returns a `{ eventName,
 * customProperties }` shape ready to spread into `googleAnalytics4Event(...)`. Property names
 * (including `preferd_method`) match the Jira NHS20-8253 acceptance-criteria table exactly —
 * not a typo to fix. `position` is provided by the caller (see
 * pages/communityDetail/components/eventTracking/) — this module has no opinion on what it means.
 *
 * @module leadGenerators/services/googleAnalytics4EventBuilder
 */

/**
 * @param {object} params
 * @param {string} params.buttonName - 'book_appointment' | 'contact_sales_center'
 * @param {string} params.position
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildFormOpenEvent({ buttonName, position }) {
  return {
    eventName: 'form_open',
    customProperties: {
      button_name: buttonName,
      position,
    },
  };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {string} [params.date] - tour date, RequestATourForm only
 * @param {number} params.timeToSubmit - seconds
 * @param {string} [params.preferredContactMethod]
 * @param {string} [params.realtorQuestion]
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildGenerateLeadEvent({
  buttonName, position, date, timeToSubmit,
  preferredContactMethod, realtorQuestion,
}) {
  const customProperties = {
    button_name: buttonName,
    position,
    time_to_submit: timeToSubmit,
    preferd_method: preferredContactMethod,
    realtor_question: realtorQuestion,
  };

  if (date) {
    customProperties.lead_form_topics = date;
  }

  return { eventName: 'generate_lead', customProperties };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {'x_button'|'outside_click'|'escape'} params.closeTarget
 * @param {number} params.fieldsCompletedCount
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildFormCloseEvent({ buttonName, position, closeTarget, fieldsCompletedCount }) {
  const customProperties = {
    button_name: buttonName,
    position,
    click_target: closeTarget,
    click_target2: fieldsCompletedCount,
  };

  // click_target1 reflects whether the user actually entered data (fieldsCompletedCount > 0), not
  // just whether they focused a field — formStarted (leadFormTrackingState.js) goes true on focus
  // alone, so it can't answer "did they fill anything in". Per NHS20-9021 UAT feedback, GA4 wants
  // the string 'form_started' here (not a boolean); omitted (not sent as blank) when false,
  // matching this module's other GA4 custom properties (e.g. buildNotifyModalClosedEvent).
  if (fieldsCompletedCount > 0) {
    customProperties.click_target1 = 'form_started';
  }

  return { eventName: 'form_close', customProperties };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {number} params.timeSpent - seconds
 * @param {string|null} params.lastFieldInteracted
 * @param {boolean} params.hasValidationError
 * @param {'invalid_format'|'required_missing'} [params.errorType]
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLeadFormAbandonedEvent({
  buttonName, position, timeSpent, lastFieldInteracted,
  hasValidationError, errorType,
}) {
  const customProperties = {
    button_name: buttonName,
    position,
    time_spent: timeSpent,
  };

  if (lastFieldInteracted) {
    customProperties.click_target1 = `last_field_${lastFieldInteracted}`;
  }

  if (hasValidationError) {
    customProperties.click_target2 = `error_type_${errorType}`;
  }

  return { eventName: 'lead_form_abandoned', customProperties };
}

/**
 * @param {object} params
 * @param {string} params.buttonName
 * @param {string} params.position
 * @param {string} params.errorType - 'validation'
 * @returns {{ eventName: string, customProperties: object }}
 */
export function buildLeadFormSubmissionFailedEvent({ buttonName, position, errorType }) {
  return {
    eventName: 'lead_form_submission_failed',
    customProperties: {
      button_name: buttonName,
      position,
      click_target2: `error_type_${errorType}`,
    },
  };
}
